import { useEffect, useRef, useState } from 'react'
import type { FrontmatterPatch, Frontmatter } from '../api'

/**
 * The page's title and its one-line description, at the top of the document.
 *
 * They were fields in the inspector, which is a panel that disappears below
 * 1180px — so on a laptop in a split window, and on every tablet, the two
 * things a reader meets FIRST could not be edited at all. They are also not
 * settings: the title is the page's heading and its link in search results,
 * and the description is the sentence under it. A writer types them where they
 * will be read, in the order they will be read.
 *
 * Saved on blur rather than on every keystroke, and only when they changed:
 * a frontmatter write rewrites the whole file and moves its revision, so one
 * per keystroke would fight the body's own autosave.
 *
 * The title is a `textarea` that grows, not an `input`: a long title in a
 * one-line box scrolls sideways under the cursor, and a heading is exactly the
 * thing somebody wants to see all of while writing it.
 */
export function DocHead({
  path,
  frontmatter,
  readOnly,
  onSave,
}: {
  /** Which page this is about: a change of page resets what is being typed. */
  path: string
  frontmatter: Frontmatter | null
  readOnly: boolean
  onSave: (patch: FrontmatterPatch) => void | Promise<void>
}) {
  const saved = {
    title: (frontmatter?.title as string | undefined) ?? '',
    description: (frontmatter?.description as string | undefined) ?? '',
  }
  const [title, setTitle] = useState(saved.title)
  const [description, setDescription] = useState(saved.description)
  const box = useRef<HTMLTextAreaElement>(null)

  /*
   * What the file says wins, whenever the file changes underneath.
   *
   * Keyed on the path and on the saved values, so a rename from the tree or
   * somebody else's edit arrives here — while what is being typed survives,
   * because a save is what changes the saved value.
   */
  useEffect(() => {
    setTitle(saved.title)
    setDescription(saved.description)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, saved.title, saved.description])

  /** Grow to fit, so no heading is ever half-visible. */
  useEffect(() => {
    const node = box.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }, [title])

  const commit = (field: 'title' | 'description', value: string) => {
    const next = value.trim()
    if (next === saved[field]) return
    // An emptied field is REMOVED rather than stored as "": a page with no
    // description should have none, not one that is blank.
    void onSave({ [field]: next === '' ? null : next } as FrontmatterPatch)
  }

  return (
    <header className="dochead">
      <textarea
        ref={box}
        className="dochead__title"
        rows={1}
        placeholder="Untitled page"
        aria-label="Page title"
        value={title}
        readOnly={readOnly}
        onChange={(e) => setTitle(e.target.value.replace(/\n/g, ''))}
        onBlur={() => commit('title', title)}
        /* Enter belongs to the document below, not to a new line in a heading. */
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
      />
      <textarea
        className="dochead__desc"
        rows={1}
        placeholder="One sentence, shown under the title in search results"
        aria-label="Page description"
        value={description}
        readOnly={readOnly}
        onChange={(e) => setDescription(e.target.value.replace(/\n/g, ''))}
        onBlur={() => commit('description', description)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
      />
    </header>
  )
}
